import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import BranchService from "../../../services/KangarooServices/BranchService";
import MediaService from "../../../services/KangarooServices/MediaServices";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MediaModal from "../../PagesSection/Component/MediaModal";
import { imageBaseApi } from "../../../utilis/imageHelper";

const BranchFormPage = () => {
  const [branchData, setBranchData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [mediaData, setMediaData] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();

  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);

  const fetchMedia = async (page = 1, size = 1000) => {
    try {
      const response = await MediaService.getList({ page, pageSize: size });
      setMediaData(response);
      return response;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  // Fetch branch details if updating
  useEffect(() => {
    if (id) {
      const fetchDataById = async () => {
        try {
          const res = await BranchService.getDetail(id);
          console.log("Branch detail response:", res);
          setBranchData(res);

          // If branch has a mediaId, fetch the media details
          if (res.mediaId) {
            // First check if mediaItems array exists in response
            if (res.mediaItems && res.mediaItems.length > 0) {
              const media = res.mediaItems.find((m) => m.id === res.mediaId);
              if (media) {
                console.log("Found media in mediaItems:", media);
                setSelectedMedia(media);
              }
            } else {
              // If mediaItems not in response, fetch from media list
              const mediaList = await fetchMedia();
              if (mediaList && mediaList.items) {
                const media = mediaList.items.find((m) => m.id === res.mediaId);
                if (media) {
                  console.log("Found media in mediaList:", media);
                  setSelectedMedia(media);
                }
              }
            }
          }
        } catch (err) {
          console.error("Error fetching branch details:", err);
        }
      };
      fetchDataById();
    }
  }, [id]);

  useEffect(() => {
    if (!id) {
      fetchMedia();
    }
  }, [id]);

  // Validation Schema
  const validationSchema = Yup.object({
    OfficeName: Yup.string().required("Office Name is required"),
    Country: Yup.string().required("Country is required"),
    City: Yup.string().required("City is required"),
    Email: Yup.string().email("Invalid email"),
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      OfficeName: branchData?.officeName || "",
      OfficeHeadName: branchData?.officeHeadName || "",
      Country: branchData?.country || "",
      City: branchData?.city || "",
      GoogleMapUrl: branchData?.googleMapUrl || "",
      Address: branchData?.address || "",
      OpeningHours: branchData?.openingHours || "",
      Phone: branchData?.phone || "",
      Email: branchData?.email || "",
      IsActive: branchData?.isActive ?? true,
      mediaId: branchData?.mediaId || null,
      orderIndex: branchData?.orderIndex,
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log("Submitting values:", values);
      console.log("Selected media:", selectedMedia);

      try {
        setLoading(true);
        const payload = {
          ...values,
          mediaId: selectedMedia?.id || null,
        };

        console.log("Payload to send:", payload);

        if (id) {
          await BranchService.update(id, payload);
          toast.success("Branch updated successfully!");
        } else {
          await BranchService.create(payload);
          toast.success("Branch created successfully!");
        }
        navigate("/branch");
      } catch (err) {
        console.error("Error saving branch:", err);
        toast.error("Failed to save branch");
      } finally {
        setLoading(false);
      }
    },
  });

  const breadcrumbItems = [{ title: "Back to List", link: "/branch" }];

  return (
    <Container fluid>
      <div className="page-content">
        <BreadCrumb
          title="Branch Form"
          pageTitle="Branch Office"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button
              type="submit"
              className="btn btn-success"
              disabled={loading}
            >
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/branch")}
              disabled={loading}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Office Name */}
                  <div className="mb-3">
                    <Label>
                      Office Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="OfficeName"
                      value={formik.values.OfficeName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Office Name"
                    />
                    {formik.touched.OfficeName && formik.errors.OfficeName && (
                      <div className="text-danger mt-1">
                        {formik.errors.OfficeName}
                      </div>
                    )}
                  </div>

                  {/* Office Head Name */}
                  <div className="mb-3">
                    <Label>Office Head Name</Label>
                    <Input
                      type="text"
                      name="OfficeHeadName"
                      value={formik.values.OfficeHeadName}
                      onChange={formik.handleChange}
                      placeholder="Enter Office Head Name"
                    />
                  </div>

                  {/* Country */}
                  <div className="mb-3">
                    <Label>
                      Country <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="Country"
                      value={formik.values.Country}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Country"
                    />
                    {formik.touched.Country && formik.errors.Country && (
                      <div className="text-danger mt-1">
                        {formik.errors.Country}
                      </div>
                    )}
                  </div>

                  {/* City */}
                  <div className="mb-3">
                    <Label>
                      City <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="City"
                      value={formik.values.City}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter City"
                    />
                    {formik.touched.City && formik.errors.City && (
                      <div className="text-danger mt-1">
                        {formik.errors.City}
                      </div>
                    )}
                  </div>

                  {/* Address */}
                  <div className="mb-3">
                    <Label>Address</Label>
                    <Input
                      type="text"
                      name="Address"
                      value={formik.values.Address}
                      onChange={formik.handleChange}
                      placeholder="Enter Address"
                    />
                  </div>

                  {/* Opening Hours */}
                  <div className="mb-3">
                    <Label>Opening Hours</Label>
                    <Input
                      type="text"
                      name="OpeningHours"
                      value={formik.values.OpeningHours}
                      onChange={formik.handleChange}
                      placeholder="Enter Opening Hours"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <CardBody>
                  {/* Branch Image/Media */}

                  <Label style={{ cursor: "pointer" }}>Image</Label>
                  {selectedMedia ? (
                    <div className="mt-2 text-center">
                      {selectedMedia.mimeType?.startsWith("image") && (
                        <img
                          src={`${imageBaseApi}${selectedMedia?.url}`}
                          alt={selectedMedia.fileName}
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                          }}
                        />
                      )}
                      {selectedMedia.mimeType === "application/pdf" && (
                        <a
                          href={selectedMedia.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block mt-2"
                        >
                          📄 {selectedMedia.fileName}
                        </a>
                      )}
                      {!selectedMedia.mimeType?.startsWith("image") &&
                        selectedMedia.mimeType !== "application/pdf" && (
                          <span className="d-block mt-2">
                            ❓ {selectedMedia.fileName}
                          </span>
                        )}
                      <div className="d-flex gap-2 mt-2 justify-content-center">
                        <Button outline onClick={toggleMedia} size="sm">
                          <i className="ri-edit-line"></i>
                        </Button>
                        <Button
                          outline
                          size="sm"
                          color="danger"
                          onClick={() => setSelectedMedia(null)}
                        >
                          <i className="ri-delete-bin-5-line"></i>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded text-center bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-1 text-muted">
                        No image selected
                      </h6>
                      <h6 className="mb-2 text-muted">800 x 800</h6>
                      <Button
                        outline
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={toggleMedia}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose Image
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label>Order Index</Label>
                    <Input
                      type="number"
                      name="orderIndex"
                      value={formik.values.orderIndex}
                      onChange={formik.handleChange}
                      placeholder="Enter Order Index"
                    />
                  </div>
                  {/* Phone */}
                  <div className="mb-3">
                    <Label>Phone</Label>
                    <Input
                      type="text"
                      name="Phone"
                      value={formik.values.Phone}
                      onChange={formik.handleChange}
                      placeholder="Enter Phone Number"
                    />
                  </div>

                  {/* Email */}
                  <div className="mb-3">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      name="Email"
                      value={formik.values.Email}
                      onChange={formik.handleChange}
                      placeholder="Enter Email"
                    />
                    {formik.touched.Email && formik.errors.Email && (
                      <div className="text-danger mt-1">
                        {formik.errors.Email}
                      </div>
                    )}
                  </div>

                  {/* Google Map URL */}
                  <div className="mb-3">
                    <Label>Google Map URL</Label>
                    <Input
                      type="url"
                      name="GoogleMapUrl"
                      value={formik.values.GoogleMapUrl}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Enter Google Map URL"
                    />
                    {formik.touched.GoogleMapUrl &&
                      formik.errors.GoogleMapUrl && (
                        <div className="text-danger mt-1">
                          {formik.errors.GoogleMapUrl}
                        </div>
                      )}
                  </div>

                  {/* Is Active */}
                  <div className="mb-3">
                    <Label check>
                      <Input
                        type="checkbox"
                        name="IsActive"
                        checked={formik.values.IsActive}
                        onChange={(e) =>
                          formik.setFieldValue("IsActive", e.target.checked)
                        }
                      />{" "}
                      <strong>Is Active</strong>
                    </Label>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        <MediaModal
          isOpen={isMediaOpen}
          toggle={toggleMedia}
          mediaData={mediaData}
          selectedMedia={selectedMedia}
          setSelectedMedia={setSelectedMedia}
        />
      </div>
    </Container>
  );
};

export default BranchFormPage;
